import React from 'react';
import type { NebulaMessage } from '../../../../types/nebula-insights';
import {
  formatDateTime,
  formatMessageContentForDisplay,
  getMessageRoleDisplay,
  getMessageVisualizations,
} from '../utils';
import MessageMarkdown from './MessageMarkdown';
import MessageVisualizations from './MessageVisualizations';

interface MessageThreadProps {
  messages: NebulaMessage[];
}

const MessageThread: React.FC<MessageThreadProps> = ({ messages }) => {
  if (messages.length === 0) {
    return (
      <p className="text-sm text-gray-500 font-family-messina-sans text-center py-8">
        No messages in this conversation
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((message) => {
        const inputTokens = message.tokenCount?.input;
        const outputTokens = message.tokenCount?.output;
        const hasTokenInfo = inputTokens != null || outputTokens != null;
        const roleDisplay = getMessageRoleDisplay(message.role);
        const RoleIcon = roleDisplay.icon;
        const body = formatMessageContentForDisplay(message.content);
        const visualizations = getMessageVisualizations(message);

        return (
          <div
            key={message.id}
            className={`rounded-lg border border-gray-200 border-l-4 p-4 ${roleDisplay.cardClass}`}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium font-family-messina-sans ${roleDisplay.badgeClass}`}
              >
                <RoleIcon size={13} strokeWidth={2} aria-hidden />
                {roleDisplay.label}
              </span>
              <span className="text-[10px] text-gray-500 font-family-messina-sans shrink-0">
                {formatDateTime(message.createdAt)}
              </span>
            </div>
            {body ? (
              <MessageMarkdown content={body} isUser={message.role === 'user'} />
            ) : visualizations.length === 0 ? (
              <p className="text-sm text-gray-400 font-family-messina-sans italic">
                No message text
              </p>
            ) : null}
            <MessageVisualizations visualizations={visualizations} />
            {(message.model || hasTokenInfo) && (
              <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-2">
                {message.model && (
                  <span className="px-2 py-0.5 rounded bg-gray-100 text-[10px] text-gray-600 font-family-messina-sans">
                    {message.model}
                  </span>
                )}
                {hasTokenInfo && (
                  <span className="px-2 py-0.5 rounded bg-gray-100 text-[10px] text-gray-600 font-family-messina-sans">
                    ↑ {String(inputTokens ?? '—')} · ↓ {String(outputTokens ?? '—')} tokens
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MessageThread;
